import { type ReactNode } from 'react';

import { Autocomplete, type AutocompleteProps } from '..';

import {
  AutocompleteInput,
  type AutocompleteInputSlotProps,
} from './autocomplete-input/autocomplete-input';
import { ReactVirtualListbox } from './react-virtual-listbox/react-virtual-listbox';

export type AutocompleteSimpleOption = {
  label: string;
  value: string;
};

type AutocompleteBaseProps = AutocompleteProps<
  AutocompleteSimpleOption,
  false,
  boolean,
  false
>;

type SlotProps = {
  input?: AutocompleteInputSlotProps;
};

export interface AutocompleteSimpleProps
  extends Omit<
    AutocompleteBaseProps,
    | 'disableListWrap'
    | 'fullWidth'
    | 'isOptionEqualToValue'
    | 'renderInput'
    | 'renderOption'
    | 'ListboxComponent'
    | 'ListboxProps'
    | 'ChipProps'
    | 'clearIcon'
    | 'componentsProps'
    | 'freeSolo'
    | 'groupBy'
    | 'includeInputInList'
    | 'multiple'
    | 'PaperComponent'
    | 'PopperComponent'
    | 'renderGroup'
    | 'slotProps'
  > {
  helperText?: string;
  /**
   * If true, the label becomes an aria-label set on the input field.
   */
  hideLabel?: boolean;
  label: string;
  placeholder?: string;
  slotProps?: SlotProps;
  /**
   * Options limit before switching to virtualization.
   * @default 100
   */
  virtualizationLimit?: number;
}

const isOptionEqualToValue = (
  option: AutocompleteSimpleOption,
  value: AutocompleteSimpleOption,
) => option.value === value.value;

export const AutocompleteSimple = ({
  helperText,
  hideLabel,
  label,
  options,
  placeholder,
  slotProps,
  virtualizationLimit = 100,
  ...rest
}: AutocompleteSimpleProps) => {
  if (options.length > virtualizationLimit) {
    return (
      <Autocomplete
        disableListWrap
        options={options}
        isOptionEqualToValue={isOptionEqualToValue}
        renderInput={(params) => (
          <AutocompleteInput
            hideLabel={hideLabel}
            helperText={helperText}
            label={label}
            params={params}
            placeholder={placeholder}
            slotProps={slotProps?.input}
          />
        )}
        // Cannot render node here but need to pass all these props to the ListboxComponent
        renderOption={(props, option) => [props, option] as ReactNode}
        ListboxComponent={ReactVirtualListbox}
        ListboxProps={{
          sx: {
            ul: {
              padding: 0,
              margin: 0,
            },
          },
        }}
        {...rest}
      />
    );
  }

  return (
    <Autocomplete
      options={options}
      isOptionEqualToValue={isOptionEqualToValue}
      renderInput={(params) => (
        <AutocompleteInput
          hideLabel={hideLabel}
          helperText={helperText}
          label={label}
          params={params}
          placeholder={placeholder}
          slotProps={slotProps?.input}
        />
      )}
      {...rest}
    />
  );
};
